// frontend/src/components/AdminDashboard.js
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  getAllUsers,
  runAlgorithm,
  runRuralExchange,
  getLotteryModels,
  registerSecondStageAssignments,
  downloadAllHospitalReportsPdf,
  getSchedules,
  createSchedule,
  updateSchedule,
  deleteSchedule,
  updateUserAdminStatus,
  getCohorts,
} from '../services/api';
import AdminCohortManager from './AdminCohortManager';
import { ArrowLeft, Calendar, Trash2, Edit2, Plus } from 'lucide-react';

const algorithms = [
  { name: 'ラウンドロビン', key: 'roundrobinranking' },
  { name: '地域医療希望配属', key: 'rural' },
  { name: '一次希望配属', key: 'firstChoice' },
  { name: '二次希望配属', key: 'secondChoice' },
  { name: '交換希望', key: 'exchange' },
];

const AdminDashboard = () => {
  const [dates, setDates] = useState({
    roundrobinrankingStart: '2024-07-21T09:00',
    roundrobinrankingEnd: '2024-07-21T09:00',
    ruralStart: '2024-07-21T09:00',
    ruralEnd: '2024-07-21T09:00',
    firstChoiceStart: '2024-07-21T09:00',
    firstChoiceEnd: '2024-07-21T09:00',
    secondChoiceStart: '2024-07-21T09:00',
    secondChoiceEnd: '2024-07-21T09:00',
    exchangeStart: '2024-07-21T09:00',
    exchangeEnd: '2024-07-21T09:00',
  });

  const [users, setUsers] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [selectedCohortId, setSelectedCohortId] = useState(null);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [userError, setUserError] = useState(null);
  const [executionStatus, setExecutionStatus] = useState({});

  const [lotteryModels, setLotteryModels] = useState([]);
  const [selectedModelId, setSelectedModelId] = useState(null);
  const [registerStatus, setRegisterStatus] = useState('');

  const [showUsers, setShowUsers] = useState(false);
  const [isGeneratingReportsPdf, setIsGeneratingReportsPdf] = useState(false);
  const [reportsPdfMessage, setReportsPdfMessage] = useState('');
  const [reportsPdfError, setReportsPdfError] = useState('');

  const [schedules, setSchedules] = useState([]);
  const [newSchedule, setNewSchedule] = useState({ title: '', start_date: '', end_date: '', description: '' });
  const [editingSchedule, setEditingSchedule] = useState(null);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

  useEffect(() => {
    fetchUsers();
    fetchCohorts();
    fetchLotteryModels();
    fetchSchedules();
  }, []);

  const fetchSchedules = async () => {
    try {
      const data = await getSchedules();
      setSchedules(data);
    } catch (error) {
      console.error('Failed to fetch schedules:', error);
    }
  };

  const handleCreateSchedule = async () => {
    if (!newSchedule.title || !newSchedule.start_date || !newSchedule.end_date) {
      alert('タイトルと開始・終了日時は必須です');
      return;
    }
    // Convert local datetime inputs to UTC ISO strings
    const payload = {
      ...newSchedule,
      start_date: new Date(newSchedule.start_date).toISOString(),
      end_date: new Date(newSchedule.end_date).toISOString(),
      // date is not used anymore but sending null or just start_date if needed for legacy
      date: new Date(newSchedule.start_date).toISOString()
    };

    try {
      await createSchedule(payload);
      setNewSchedule({ title: '', start_date: '', end_date: '', description: '' });
      fetchSchedules();
    } catch (error) {
      console.error('Failed to create schedule:', error);
      alert('スケジュールの作成に失敗しました');
    }
  };

  const handleUpdateSchedule = async () => {
    if (!editingSchedule || !editingSchedule.title || !editingSchedule.start_date || !editingSchedule.end_date) {
      alert('タイトルと開始・終了日時は必須です');
      return;
    }
    // Convert local inputs to UTC
    const payload = {
      ...editingSchedule,
      start_date: new Date(editingSchedule.start_date).toISOString(),
      end_date: new Date(editingSchedule.end_date).toISOString(),
    };

    try {
      await updateSchedule(editingSchedule.id, payload);
      setEditingSchedule(null);
      // ...
    } catch (error) {
      console.error('Failed to update schedule:', error);
      alert('スケジュールの更新に失敗しました');
    }
  };

  const handleDeleteSchedule = async (id) => {
    if (!window.confirm('本当に削除しますか？')) return;
    try {
      await deleteSchedule(id);
      fetchSchedules();
    } catch (error) {
      console.error('Failed to delete schedule:', error);
      alert('スケジュールの削除に失敗しました');
    }
  };

  const fetchUsers = async () => {
    setIsLoadingUsers(true);
    setUserError(null);
    try {
      const usersData = await getAllUsers();
      setUsers(usersData);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setUserError('ユーザー情報の取得に失敗しました。');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const fetchCohorts = async () => {
    try {
      const data = await getCohorts();
      const sorted = data.sort((a, b) => {
        if (a.name > b.name) return -1;
        if (a.name < b.name) return 1;
        return 0;
      });
      setCohorts(sorted);
      if (sorted.length > 0) {
        setSelectedCohortId(sorted[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch cohorts:', error);
    }
  };

  const fetchLotteryModels = async () => {
    try {
      const models = await getLotteryModels();
      setLotteryModels(models);
      if (models.length > 0) setSelectedModelId(models[0].id);
    } catch (error) {
      console.error('抽選モデルの取得に失敗しました:', error);
    }
  };

  const handleDateChange = (event) => {
    const { name, value } = event.target;
    setDates((prev) => ({ ...prev, [name]: value }));
  };

  // Helper to convert UTC string (from DB) to "YYYY-MM-DDTHH:mm" (local time) for input[type="datetime-local"]
  const toLocalISOString = (utcDateString) => {
    if (!utcDateString) return '';
    const date = new Date(utcDateString);
    // Get local timezone offset in minutes and reverse it
    const offset = date.getTimezoneOffset() * 60000;
    const localDate = new Date(date.getTime() - offset);
    return localDate.toISOString().slice(0, 16);
  };

  const handleAdminToggle = async (user) => {
    if (!window.confirm(`${user.username}の管理者権限を${user.is_admin ? '解除' : '付与'}しますか？`)) return;
    try {
      await updateUserAdminStatus(user.firebase_uid, !user.is_admin);
      fetchUsers();
    } catch (error) {
      console.error('Failed to update admin status:', error);
      alert('管理者権限の更新に失敗しました');
    }
  };

  const handleSaveDates = () => {
  };

  const handleRunAlgorithm = async (algorithmKey) => {
    setExecutionStatus((prev) => ({ ...prev, [algorithmKey]: '実行中です' }));
    try {
      await runAlgorithm(algorithmKey);
      setExecutionStatus((prev) => ({ ...prev, [algorithmKey]: '完了しました' }));
      setTimeout(() => {
        setExecutionStatus((prev) => {
          const next = { ...prev };
          delete next[algorithmKey];
          return next;
        });
      }, 3000);
    } catch (err) {
      console.error(`Failed to run ${algorithmKey} algorithm:`, err);
      setExecutionStatus((prev) => ({ ...prev, [algorithmKey]: '失敗しました' }));
    }
  };

  const handleRunRuralExchange = async () => {
    const key = 'rural';
    setExecutionStatus((prev) => ({ ...prev, [key]: '実行中です' }));
    try {
      await runRuralExchange();
      setExecutionStatus((prev) => ({ ...prev, [key]: '完了しました' }));
      setTimeout(() => {
        setExecutionStatus((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }, 3000);
    } catch (err) {
      console.error('Failed to run rural exchange:', err);
      setExecutionStatus((prev) => ({ ...prev, [key]: '失敗しました' }));
    }
  };

  const handleModelChange = (event) => {
    setSelectedModelId(parseInt(event.target.value, 10));
  };

  const handleRegisterSecondStage = async () => {
    if (!selectedModelId) {
      alert('抽選モデルを選択してください。');
      return;
    }
    setRegisterStatus('登録中...');
    try {
      await registerSecondStageAssignments(selectedModelId);
      setRegisterStatus('登録が完了しました。');
      setTimeout(() => setRegisterStatus(''), 3000);
    } catch (error) {
      console.error('二次抽選結果の登録に失敗しました:', error);
      setRegisterStatus('登録に失敗しました。');
    }
  };

  const handleDownloadAllReportsPdf = async () => {
    setIsGeneratingReportsPdf(true);
    setReportsPdfMessage('');
    setReportsPdfError('');
    try {
      const { blob, filename } = await downloadAllHospitalReportsPdf();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'HospitalReports_All.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setReportsPdfMessage('PDFをダウンロードしました。');
      setTimeout(() => setReportsPdfMessage(''), 4000);
    } catch (error) {
      console.error('Failed to download all hospital report PDFs:', error);
      setReportsPdfError(error?.message || 'PDFの生成に失敗しました。');
    } finally {
      setIsGeneratingReportsPdf(false);
    }
  };

  const toggleShowUsers = () => setShowUsers((prev) => !prev);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') direction = 'descending';
    setSortConfig({ key, direction });
  };

  const sortedUsers = useMemo(() => {
    let sortableUsers = [...users];
    if (sortConfig.key !== null) {
      sortableUsers.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }
    return sortableUsers;
  }, [users, sortConfig]);

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="p-2 -ml-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-900 transition-colors"
              aria-label="ホームに戻る"
            >
              <ArrowLeft size={24} />
            </Link>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Admin Console</p>
              <h1 className="text-2xl font-bold text-gray-900">管理ダッシュボード</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* 年度管理セクション */}
        <AdminCohortManager />

        {/* 日程管理セクション */}
        <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                日程管理
              </h2>
              <p className="text-sm text-gray-500">ダッシュボードに表示する日程を管理します。</p>
            </div>
          </div>

          <div className="grid gap-6 md:grid-cols-2 mb-8">
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="font-bold text-gray-900">新規作成</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">タイトル</label>
                  <input
                    type="text"
                    value={newSchedule.title}
                    onChange={(e) => setNewSchedule({ ...newSchedule, title: e.target.value })}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    placeholder="例: 1次希望登録締切"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">開始日時</label>
                  <input
                    type="datetime-local"
                    value={newSchedule.start_date}
                    onChange={(e) => setNewSchedule({ ...newSchedule, start_date: e.target.value })}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">終了日時（締切）</label>
                  <input
                    type="datetime-local"
                    value={newSchedule.end_date}
                    onChange={(e) => setNewSchedule({ ...newSchedule, end_date: e.target.value })}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">説明(任意)</label>
                  <textarea
                    value={newSchedule.description}
                    onChange={(e) => setNewSchedule({ ...newSchedule, description: e.target.value })}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    rows={2}
                  />
                </div>
                <Button onClick={handleCreateSchedule} className="w-full bg-blue-600 text-white hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" /> 追加
                </Button>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-bold text-gray-900">登録済み日程一覧</h3>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {schedules.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">登録された日程はありません</p>
                ) : (
                  schedules.map((schedule) => (
                    <div key={schedule.id} className="p-3 border border-gray-200 rounded-lg bg-white hover:shadow-sm transition-shadow">
                      {editingSchedule?.id === schedule.id ? (
                        <div className="space-y-3">
                          <input
                            type="text"
                            value={editingSchedule.title}
                            onChange={(e) => setEditingSchedule({ ...editingSchedule, title: e.target.value })}
                            className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
                          />
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              type="datetime-local"
                              value={editingSchedule.start_date ? (editingSchedule.start_date.length > 16 ? toLocalISOString(editingSchedule.start_date) : editingSchedule.start_date) : ''}
                              onChange={(e) => setEditingSchedule({ ...editingSchedule, start_date: e.target.value })}
                              className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
                            />
                            <input
                              type="datetime-local"
                              value={editingSchedule.end_date ? (editingSchedule.end_date.length > 16 ? toLocalISOString(editingSchedule.end_date) : editingSchedule.end_date) : ''}
                              onChange={(e) => setEditingSchedule({ ...editingSchedule, end_date: e.target.value })}
                              className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
                            />
                          </div>
                          <textarea
                            value={editingSchedule.description || ''}
                            onChange={(e) => setEditingSchedule({ ...editingSchedule, description: e.target.value })}
                            className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
                            rows={2}
                          />
                          <div className="flex gap-2 justify-end">
                            <Button size="sm" variant="outline" onClick={() => setEditingSchedule(null)}>キャンセル</Button>
                            <Button size="sm" className="bg-blue-600 text-white" onClick={handleUpdateSchedule}>保存</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-bold text-gray-900">{schedule.title}</h4>
                            <p className="text-sm text-blue-600 font-mono">
                              {schedule.start_date ? new Date(schedule.start_date).toLocaleString('ja-JP', {
                                month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
                              }) : '未設定'}
                              {' 〜 '}
                              {schedule.end_date ? new Date(schedule.end_date).toLocaleString('ja-JP', {
                                month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
                              }) : '未設定'}
                            </p>
                            {schedule.description && <p className="text-xs text-gray-500 mt-1">{schedule.description}</p>}
                          </div>
                          <div className="flex gap-1">
                            <button
                              onClick={() => setEditingSchedule(schedule)}
                              className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteSchedule(schedule.id)}
                              className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">アルゴリズム実行</h2>
              <p className="text-sm text-gray-500">スケジュールの設定とアルゴリズムの手動実行を行います。</p>
            </div>
            <Button variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50" onClick={handleSaveDates}>
              スケジュールを保存
            </Button>
          </div>

          <div className="space-y-4">
            {algorithms.map((algorithm) => (
              <div
                key={algorithm.key}
                className="grid gap-4 md:grid-cols-[200px,1fr,1fr,auto] items-center p-4 rounded-md border border-gray-200 bg-gray-50/50"
              >
                <div>
                  <p className="text-sm font-bold text-gray-900">{algorithm.name}</p>
                  <p className="text-xs text-gray-500 font-mono">{algorithm.key}</p>
                </div>
                <div className="flex flex-col">
                  <label className="text-xs text-gray-500 mb-1">開始日時</label>
                  <input
                    type="datetime-local"
                    name={`${algorithm.key}Start`}
                    value={dates[`${algorithm.key}Start`]}
                    onChange={handleDateChange}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-xs text-gray-500 mb-1">終了日時</label>
                  <input
                    type="datetime-local"
                    name={`${algorithm.key}End`}
                    value={dates[`${algorithm.key}End`]}
                    onChange={handleDateChange}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div className="flex flex-col gap-2 min-w-[180px] justify-end items-end">
                  <div className="flex items-center gap-3">
                    {executionStatus[algorithm.key] && (
                      <span
                        className={`text-xs font-medium ${executionStatus[algorithm.key] === '完了しました'
                          ? 'text-emerald-700'
                          : executionStatus[algorithm.key] === '失敗しました'
                            ? 'text-red-700'
                            : 'text-blue-700'
                          }`}
                      >
                        {executionStatus[algorithm.key]}
                      </span>
                    )}
                    <Button
                      onClick={() => handleRunAlgorithm(algorithm.key)}
                      disabled={executionStatus[algorithm.key] === '実行中です'}
                      className="bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-300"
                    >
                      実行
                    </Button>
                  </div>
                  {algorithm.key === 'rural' && (
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={handleRunRuralExchange}
                        className="bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-emerald-300"
                      >
                        地域医療交換を実行
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-4">
            <div>
              <h2 className="text-lg font-bold text-gray-900">定員管理</h2>
              <p className="text-sm text-gray-500">病院・科・タームごとの定員数を管理します。</p>
            </div>
            <div className="flex items-center justify-end pt-2">
              <Link
                to="/admin/capacity"
                className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                定員管理
              </Link>
            </div>
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-4">
            <div>
              <h2 className="text-lg font-bold text-gray-900">支払い状況</h2>
              <p className="text-sm text-gray-500">各ユーザーの支払い状況を確認します。</p>
            </div>
            <div className="flex items-center justify-end pt-2">
              <Link
                to="/admin/payment-status"
                className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                支払い状況ページ
              </Link>
            </div>
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-4">
            <div>
              <h2 className="text-lg font-bold text-gray-900">固定割り当て管理</h2>
              <p className="text-sm text-gray-500">1次希望抽選前の固定割り当てを管理します。</p>
            </div>
            <div className="flex items-center justify-end pt-2">
              <Link
                to="/admin/assignments/fixed"
                className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                管理ページへ
              </Link>
            </div>
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-4">
            <div>
              <h2 className="text-lg font-bold text-gray-900">二次抽選結果の登録</h2>
              <p className="text-sm text-gray-500">抽選モデルを選択して結果を確定させます。</p>
            </div>
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">抽選モデル選択</label>
              <select
                value={selectedModelId || ''}
                onChange={handleModelChange}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              >
                {lotteryModels.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name}
                  </option>
                ))}
              </select>
              <div className="flex items-center justify-between pt-2">
                <span className={`text-sm font-medium ${registerStatus.includes('失敗') ? 'text-red-600' : 'text-emerald-600'}`}>
                  {registerStatus}
                </span>
                <Button className="bg-blue-600 text-white hover:bg-blue-700" onClick={handleRegisterSecondStage}>
                  登録を実行
                </Button>
              </div>
            </div>
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-4">
            <div>
              <h2 className="text-lg font-bold text-gray-900">病院レポートPDF</h2>
              <p className="text-sm text-gray-500">全病院のレポートを一括でPDF出力します。</p>
            </div>
            <div className="flex items-center justify-between pt-4">
              <div className="text-sm">
                {reportsPdfMessage && <div className="text-emerald-700 font-medium">{reportsPdfMessage}</div>}
                {reportsPdfError && <div className="text-red-700 font-medium">{reportsPdfError}</div>}
              </div>
              <Button
                onClick={handleDownloadAllReportsPdf}
                disabled={isGeneratingReportsPdf}
                className="bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-300"
              >
                {isGeneratingReportsPdf ? '生成中...' : 'PDF出力'}
              </Button>
            </div>
          </section>
          <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-4">
            <div>
              <h2 className="text-lg font-bold text-gray-900">病院レポート作成</h2>
              <p className="text-sm text-gray-500">病院レポートを作成します。</p>
            </div>
            <div className="flex items-center justify-end pt-2">
              <Link
                to="/admin/hospital-report"
                className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                管理ページへ
              </Link>
            </div>
          </section>


          <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-4">
            <div>
              <h2 className="text-lg font-bold text-gray-900">お知らせ管理</h2>
              <p className="text-sm text-gray-500">ダッシュボードに表示するお知らせの追加・編集・公開設定を行います。</p>
            </div>
            <div className="flex items-center justify-end pt-2">
              <Link
                to="/admin/announcements"
                className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                お知らせ管理ページへ
              </Link>
            </div>
          </section>
        </div>

        <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between cursor-pointer" onClick={toggleShowUsers}>
            <h2 className="text-lg font-bold text-gray-900">ユーザー一覧</h2>
            <span className="text-sm text-blue-600 font-medium">{showUsers ? '閉じる' : '表示する'}</span>
          </div>
          {showUsers && (
            <div className="p-0">
              {isLoadingUsers ? (
                <div className="py-12 text-center text-gray-500">読み込み中...</div>
              ) : userError ? (
                <div className="py-12 text-center text-red-600">{userError}</div>
              ) : (
                <div className="space-y-6">
                  {/* Tabs */}
                  <div className="border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8 overflow-x-auto" aria-label="Tabs">
                      {cohorts.map((cohort) => (
                        <button
                          key={cohort.id}
                          onClick={() => setSelectedCohortId(cohort.id)}
                          className={`
                            whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                            ${selectedCohortId === cohort.id
                              ? 'border-blue-500 text-blue-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                          `}
                        >
                          {cohort.name}
                        </button>
                      ))}
                      <button
                        onClick={() => setSelectedCohortId('others')}
                        className={`
                          whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                          ${selectedCohortId === 'others'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                        `}
                      >
                        その他
                      </button>
                    </nav>
                  </div>

                  {/* User Table */}
                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <Table>
                      <TableHeader className="bg-gray-50">
                        <TableRow>
                          {[
                            { key: 'username', label: 'ユーザー名' },
                            { key: 'email', label: 'メール' },
                            { key: 'rural_preference_status', label: '地域希望' },
                            { key: 'first_preference_status', label: '1次希望' },
                            { key: 'second_preference_status', label: '2次希望' },
                            { key: 'second_result_status', label: '2次結果' },
                            { key: 'hospital_exchange_preference_status', label: '交換申請' },
                            { key: 'rural_exchange_preference_status', label: '地域交換' },
                            { key: 'final_result_status', label: '最終結果' },
                            { key: 'round_robin_rankings', label: 'RRR' },
                            { key: 'rural_ranking', label: '地域順位' },
                            { key: 'is_admin', label: '管理者' },
                          ].map((col) => (
                            <TableHead
                              key={col.key}
                              className="cursor-pointer text-gray-700 font-bold whitespace-nowrap py-3"
                              onClick={() => requestSort(col.key)}
                            >
                              {col.label}
                              {sortConfig.key === col.key && (sortConfig.direction === 'ascending' ? ' ▲' : ' ▼')}
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(() => {
                          const filteredUsers = sortedUsers.filter(user => {
                            if (selectedCohortId === 'others') {
                              return !user.cohort_id;
                            }
                            return user.cohort_id === selectedCohortId;
                          });

                          if (filteredUsers.length === 0) {
                            return (
                              <TableRow>
                                <TableCell colSpan={12} className="text-center py-8 text-gray-500">
                                  該当するユーザーはいません
                                </TableCell>
                              </TableRow>
                            );
                          }

                          return filteredUsers.map((user) => (
                            <TableRow key={user.id} className="hover:bg-gray-50 border-b border-gray-100 last:border-0">
                              <TableCell className="text-gray-900 font-medium">{user.username}</TableCell>
                              <TableCell className="font-mono text-xs text-gray-600">{user.email}</TableCell>
                              {[
                                user.rural_preference_status,
                                user.first_preference_status,
                                user.second_preference_status,
                                user.second_result_status,
                                user.hospital_exchange_preference_status,
                                user.rural_exchange_preference_status,
                                user.final_result_status,
                              ].map((flag, idx) => (
                                <TableCell key={idx}>
                                  <span
                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${flag ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'
                                      }`}
                                  >
                                    {flag ? '済' : '未'}
                                  </span>
                                </TableCell>
                              ))}
                              <TableCell className="font-mono text-xs text-gray-700">{user.round_robin_rankings?.join(', ')}</TableCell>
                              <TableCell className="font-mono text-xs text-gray-700">{user.rural_ranking}</TableCell>
                              <TableCell>
                                <button
                                  onClick={() => handleAdminToggle(user)}
                                  className={`px-2 py-1 rounded text-xs font-medium transition-colors ${user.is_admin
                                    ? 'bg-purple-100 text-purple-800 hover:bg-purple-200'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                >
                                  {user.is_admin ? '管理者' : '一般'}
                                </button>
                              </TableCell>
                            </TableRow>
                          ));
                        })()}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-bold text-gray-900 mb-4">配属一覧ページへのリンク</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Link
                to="/all-user-assignments"
                className="flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 transition"
              >
                全ユーザー配属一覧
              </Link>
              <Link
                to="/final-assignments"
                className="flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 transition"
              >
                最終配属一覧
              </Link>
            </div>
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-bold text-gray-900 mb-4">送付情報</h2>
            <div className="rounded-md bg-gray-50 p-4 border border-gray-200">
              <div className="text-sm text-gray-700 space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-500">電話番号</span> <span className="font-mono">090-7860-1615</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">アカウント名</span> <span>K.Tsuchiya</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">メモ</span> <span>学籍番号を記載してください</span>
                </div>
              </div>
            </div>
            <p className="mt-4 text-xs text-gray-500">
              送付後は <a className="text-blue-600 hover:underline font-medium" href="mailto:20m.ka.tsuchiya@gmail.com">20m.ka.tsuchiya@gmail.com</a> へ連絡してください。
            </p>
          </section>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
